import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import SafeImage from "./SafeImage";
import { API } from "../context/AuthContext";

// Local YYYY-MM-DD (avoids UTC off-by-one from toISOString()).
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Storefront "Book" slide-over. Desktop = clean right-side panel, mobile =
// full-screen sheet. Collects service + date + a real bookable time, then hands
// off to the dedicated checkout page (ServiceDetail) which owns the robust
// quote / consent / payment flow. Unavailable / already-booked times are never
// shown — the /availability endpoint returns bookable slots only.
export default function BookingSheet({ service, open, onClose }) {
  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const [weekStart, setWeekStart] = useState(today);
  const [date, setDate] = useState(ymd(today));
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const nav = useNavigate();

  // Reset to today whenever the sheet opens or the service changes.
  useEffect(() => {
    if (open) {
      setWeekStart(today);
      setDate(ymd(today));
      setTime("");
      setSlots([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service?.id]);

  // Fetch bookable times immediately for the selected date.
  useEffect(() => {
    if (!open || !date || !service) { setSlots([]); setTime(""); return; }
    let alive = true;
    setLoadingSlots(true);
    setTime("");
    axios.get(`${API}/services/${service.id}/availability`, { params: { date } })
      .then((r) => { if (alive) setSlots(r.data.slots || []); })
      .catch(() => { if (alive) setSlots([]); })
      .finally(() => { if (alive) setLoadingSlots(false); });
    return () => { alive = false; };
  }, [open, date, service]);

  if (!service) return null;

  const requestMode = service.business_booking_method === "request";
  const price = Number(service.price || 0);
  const days = [0, 1, 2, 3, 4].map((i) => addDays(weekStart, i));
  const canGoPrev = ymd(weekStart) > ymd(today);

  const shiftWeek = (dir) => {
    const next = addDays(weekStart, dir * 5);
    setWeekStart(ymd(next) < ymd(today) ? today : next);
  };

  const cont = () => {
    const qs = new URLSearchParams();
    if (date) qs.set("date", date);
    if (time) qs.set("time", time);
    nav(`/services/${service.id}${qs.toString() ? `?${qs.toString()}` : ""}`);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        data-testid="booking-sheet"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0 border-l"
        style={{ background: "var(--gf-bg)", borderColor: "var(--gf-border)" }}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b" style={{ borderColor: "var(--gf-border)" }}>
          <div className="flex items-center justify-between gap-3">
            <SheetTitle className="font-display text-lg font-bold leading-tight truncate" style={{ color: "var(--gf-text)" }} data-testid="booking-sheet-heading">
              Book {service.title}
            </SheetTitle>
            <SheetDescription className="sr-only">Choose a date and time to book {service.title}.</SheetDescription>
            <button data-testid="booking-sheet-close" onClick={onClose} aria-label="Close"
              className="shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center transition-colors hover:opacity-80"
              style={{ background: "var(--gf-surface-2)", color: "var(--gf-text)" }}>
              <X size={16} />
            </button>
          </div>

          <div className="mt-4 flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl overflow-hidden shrink-0" style={{ background: "var(--gf-surface-2)" }}>
              <SafeImage src={service.image_url} alt={service.title} className="w-full h-full object-cover"
                fallback={<div className="w-full h-full" style={{ background: "var(--gf-surface-2)" }} />} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold leading-snug" style={{ color: "var(--gf-text)" }} data-testid="booking-sheet-title">{service.title}</div>
              <div className="mt-1.5 inline-flex items-center gap-1 text-xs" style={{ color: "var(--gf-text-muted)" }}>
                <Clock size={13} /> {service.duration_minutes} min
              </div>
              <div className="mt-1 font-display text-xl font-bold" style={{ color: "var(--gf-text)" }} data-testid="booking-sheet-price">${price.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Date + times */}
        <div className="px-5 py-5 flex-1 overflow-y-auto">
          {/* Horizontal date selector */}
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Select a date</label>
            <div className="flex items-center gap-1">
              <button data-testid="booking-sheet-date-prev" onClick={() => shiftWeek(-1)} disabled={!canGoPrev} aria-label="Previous days"
                className="w-8 h-8 rounded-full inline-flex items-center justify-center disabled:opacity-30 transition-colors"
                style={{ border: "1px solid var(--gf-border-strong)", color: "var(--gf-text)" }}>
                <ChevronLeft size={15} />
              </button>
              <button data-testid="booking-sheet-date-next" onClick={() => shiftWeek(1)} aria-label="Next days"
                className="w-8 h-8 rounded-full inline-flex items-center justify-center transition-colors"
                style={{ border: "1px solid var(--gf-border-strong)", color: "var(--gf-text)" }}>
                <ChevronRight size={15} />
              </button>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-5 gap-2" data-testid="booking-sheet-dates">
            {days.map((d) => {
              const v = ymd(d);
              const active = v === date;
              return (
                <button key={v} type="button" data-testid={`booking-sheet-date-${v}`} onClick={() => setDate(v)}
                  className="rounded-2xl py-2.5 flex flex-col items-center transition-colors"
                  style={{
                    background: active ? "var(--gf-accent)" : "transparent",
                    color: active ? "var(--gf-on-accent)" : "var(--gf-text)",
                    border: `1px solid ${active ? "var(--gf-accent)" : "var(--gf-border-strong)"}`,
                  }}>
                  <span className="text-[11px] font-medium" style={{ opacity: active ? 0.85 : 0.6 }}>{WD[d.getDay()]}</span>
                  <span className="text-base font-bold leading-tight">{d.getDate()}</span>
                </button>
              );
            })}
          </div>

          {/* Available times */}
          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Available times</label>
            {loadingSlots ? (
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="booking-sheet-slots-loading">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-10 rounded-xl animate-pulse" style={{ background: "var(--gf-surface-2)" }} />
                ))}
              </div>
            ) : slots.length === 0 ? (
              <div className="mt-3 text-sm p-4 rounded-2xl border text-center" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }} data-testid="booking-sheet-no-slots">
                No open times on this date. Try another day.
              </div>
            ) : (
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="booking-sheet-slots">
                {slots.map((iso) => {
                  const label = new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
                  const active = time === iso;
                  return (
                    <button key={iso} type="button" data-testid={`booking-sheet-slot-${iso}`} onClick={() => setTime(iso)}
                      className="text-sm font-medium rounded-xl py-2.5 transition-colors"
                      style={{
                        background: active ? "var(--gf-accent)" : "transparent",
                        color: active ? "var(--gf-on-accent)" : "var(--gf-text)",
                        border: `1px solid ${active ? "var(--gf-accent)" : "var(--gf-border-strong)"}`,
                      }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {requestMode && (
            <div className="mt-5 rounded-2xl border p-3 text-xs leading-relaxed" style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface-2)", color: "var(--gf-text-soft)" }}>
              This business reviews requested times before confirming. Nothing is charged until they accept.
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="px-5 py-4 border-t" style={{ borderColor: "var(--gf-border)" }}>
          <button
            data-testid="booking-sheet-continue"
            onClick={cont}
            disabled={!date || !time}
            className="w-full rounded-full font-semibold px-5 py-3.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.99] transition-transform"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
          >
            {requestMode ? "Continue to request" : "Continue to checkout"} <ArrowRight size={15} />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
